/**
 * **Add a note for the crew** — `docs/visit-page.md` §13.3, Board 9; the
 * header's **Message crew** opens the same composer in a sheet (§7.9,
 * `crew-note-sheet.tsx`), and so does the phone's green *Add note*.
 *
 * *the crew reads it on the iPad before they start* · the note · *Where it
 * lives* **This visit · Every visit at this property · <first name>,
 * everywhere** (green when chosen; This visit by default) · **Tell the crew
 * now** (on — a push to the iPads of today's or the next day's crew) · attach ·
 * **Add note**. This visit writes an `activities` note, crew-visible, linked
 * to the work item and pinned when the crew is told; the other two link to
 * the property or the contact (`POST /visits/:id/crew-notes`).
 */
import { useState } from 'react'
import type { CrewNoteComposer as Composer, CrewNoteHomeKey, CrewNoteInput } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Switch } from '../components/primitives.js'
import { useRecordUpload } from '../record/use-record-upload.js'
import { VisitGlyph } from './glyphs.js'
import { PillChip } from './crew-parts.js'
import type { VisitWrite, VisitWriteResult } from './api.js'
import { CREW_NOTE, FOR_CREW } from './copy.js'

export function CrewNoteComposerCard({
  composer, orgUnitId, write, tab, phone, variant = 'card', onWritten,
}: {
  composer: Composer
  /** The visit's branch — where an attached file is filed. */
  orgUnitId: string
  write: VisitWrite
  /** The tab whose fresh read the write should answer with. */
  tab: 'for-crew' | 'overview'
  phone: boolean
  /** `card` on the tab (Board 9); `sheet` inside Message crew's sheet, whose header carries the title. */
  variant?: 'card' | 'sheet'
  onWritten: (result: VisitWriteResult) => void
}) {
  const [text, setText] = useState('')
  const [lives, setLives] = useState<CrewNoteHomeKey>('visit')
  const [notify, setNotify] = useState(composer.tellCrewNow)
  const [fileIds, setFileIds] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const attach = useRecordUpload({
    filesAt: null,
    orgUnitId,
    canEdit: composer.can,
    describe: false,
    onUploaded: (files) => setFileIds((ids) => [...ids, ...files.map((file) => file.id)]),
  })

  async function add() {
    if (!text.trim() || busy) return
    setBusy(true)
    setError(null)
    try {
      const body: CrewNoteInput = { text: text.trim(), lives, notify, pin: notify && lives === 'visit', fileIds }
      const result = await write('POST', '/crew-notes', body, { tab })
      setText('')
      setLives('visit')
      setNotify(composer.tellCrewNow)
      setFileIds([])
      onWritten(result)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const homes: { key: CrewNoteHomeKey; label: string }[] = [
    { key: 'visit', label: CREW_NOTE.home.visit },
    ...(composer.property ? [{ key: 'property' as const, label: CREW_NOTE.home.property }] : []),
    ...(composer.contact ? [{ key: 'contact' as const, label: CREW_NOTE.home.contact(composer.contact.firstName) }] : []),
  ]

  const tellNow = (
    <button
      type="button"
      role="switch"
      aria-checked={notify}
      onClick={() => setNotify(!notify)}
      className={cn('flex shrink-0 items-center gap-1.5', phone && 'min-h-11')}
    >
      <Switch size="xs" checked={notify} onChange={setNotify} label={CREW_NOTE.tellNow} hideLabel readOnly />
      <span className="whitespace-nowrap text-[12.5px] text-text">{CREW_NOTE.tellNow}</span>
    </button>
  )
  const attachButton = (
    <button
      type="button"
      aria-label={FOR_CREW.addFile}
      title={attach.error ?? undefined}
      onClick={() => attach.openPicker({ visibility: 'crew' })}
      className={cn('inline-flex shrink-0 items-center justify-center rounded-button border',
        phone ? 'h-11 w-11' : 'h-8 w-8',
        fileIds.length > 0 ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text hover:bg-hover')}
    >
      <VisitGlyph name="attach" size={15} stroke={2} />
    </button>
  )
  const addButton = (
    <button
      type="button"
      onClick={() => void add()}
      disabled={!text.trim() || busy || attach.busy}
      className={cn('inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-control border px-3.5',
        'text-[12.5px] font-semibold', phone ? 'h-11' : 'h-8',
        'border-accent bg-accent text-white hover:bg-accent-dark',
        'disabled:cursor-not-allowed disabled:border-line disabled:bg-line-soft disabled:text-text-faint')}
    >
      {CREW_NOTE.add}
    </button>
  )

  return (
    <div
      className={cn('flex flex-col gap-2.5',
        variant === 'card' && 'rounded-record border border-line bg-surface px-4 py-3.5')}
    >
      <input {...attach.inputProps} />
      {variant === 'card' && (
        <div className={cn('flex gap-2', phone ? 'flex-col gap-0.5' : 'items-center')}>
          <b className="text-[14.5px] font-bold text-text">{CREW_NOTE.title}</b>
          <span className="text-[12px] text-text-muted">{CREW_NOTE.sub}</span>
        </div>
      )}
      <textarea
        aria-label={CREW_NOTE.title}
        value={text}
        onChange={(event) => setText(event.target.value)}
        className={cn('h-[58px] w-full resize-none rounded-button border border-line bg-surface px-3 py-2.5',
          'text-[13px] leading-[1.5] text-text outline-none focus:border-accent-line focus:shadow-[0_0_0_2px_rgb(var(--color-accent-ring))]',
          phone && 'h-[88px] text-[15px]')}
      />
      {error && <p className="m-0 text-[12px] text-danger">{error}</p>}
      {phone ? (
        <>
          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] text-text-muted">{CREW_NOTE.whereItLives}</span>
            <div className="flex flex-wrap gap-1.5">
              {homes.map((home) => (
                <PillChip key={home.key} look="phone" selected={lives === home.key} onClick={() => setLives(home.key)}>
                  {home.label}
                </PillChip>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            {tellNow}
            <span className="flex-1" />
            {attachButton}
            {addButton}
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="whitespace-nowrap text-[12px] text-text-muted">{CREW_NOTE.whereItLives}</span>
          <div className="flex flex-wrap gap-1.5">
            {homes.map((home) => (
              <PillChip key={home.key} look="pick" selected={lives === home.key} onClick={() => setLives(home.key)}>
                {home.label}
              </PillChip>
            ))}
          </div>
          <span className="ml-auto flex items-center gap-2.5">
            {tellNow}
            {attachButton}
            {addButton}
          </span>
        </div>
      )}
    </div>
  )
}
